import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import ReportParameterForm from './ReportParameterForm';

import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import SegmentedControl from 'CommonComponents/SegmentedControl';

import useModal from 'Hooks/useModal';

const MODAL_ID = 'report-parameter-form-modal';

// Labels mirror App\Support\ReportCentre\ReportCategoryTaxonomy so the front-end
// renders the same ordered structure the backend nests the catalogue into. The
// section headers use the tier labels; the rail uses the shorter group labels.
const TOP_LEVEL_LABELS = {
    user_reporting: 'User reporting',
    event_admin: 'Event admin',
};

const RAIL_GROUP_LABELS = {
    user_reporting: 'My reports',
    event_admin: 'Admin reports',
};

const FUNCTIONAL_LABELS = {
    setup: 'Set up',
    budget: 'Budget',
    actuals: 'Actuals',
    cashflow: 'Cashflow',
    workforce: 'Workforce',
    procurement: 'Procurement and contract management',
    raido: 'R.A.D.I.O.',
    project_management: 'Project Management',
    management_packs: 'Management packs',
};

// Leading glyphs for the format tags (stroke uses currentColor so they inherit the
// Badge's variant colour). Match the redesign mockup: a download arrow and a monitor.
const DownloadIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="tw-w-full tw-h-full">
        <path d="M12 3v12" />
        <path d="m7 11 5 5 5-5" />
        <path d="M4 21h16" />
    </svg>
);
const ScreenIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="tw-w-full tw-h-full">
        <rect x="2" y="4" width="20" height="13" rx="2" />
        <path d="M8 21h8" />
        <path d="M12 17v4" />
    </svg>
);

// Lock glyph for the "Event admin" badge (admin-only reports), per the mockup.
const LockIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="tw-w-full tw-h-full">
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

// Output-format tag → Badge variant + copy + icon. Mirrors the `format` value the
// backend emits ('download' → Excel file, 'screen' → viewed live on screen).
const FORMAT_BADGE = {
    download: { variant: 'success', label: 'Excel download', icon: DownloadIcon },
    screen: { variant: 'info', label: 'On-screen', icon: ScreenIcon },
};

/**
 * "Report" tab: the registry-driven report catalogue. A left category rail (the
 * two access tiers — My reports / Admin reports — with per-category counts) filters
 * a report list on the right. Every report renders as a uniform card (Dave's
 * redesign removes any "migrated vs legacy" distinction): title, one-line
 * description, an output-format tag, and a single standard Configure action.
 * Configure opens the generic ReportParameterForm for centre-generated reports and
 * navigates to the report's own screen for context-launched reports and legacy
 * link-outs. The Event-admin tier is only present in the catalogue for managers
 * (the backend applies the hard wall), so the rail/list are purely data-driven.
 */
export default function ReportCatalogue({
    catalogue = {},
    optionSources = {},
    storeUrl,
    filterSetsIndexUrl,
    filterSetsStoreUrl,
    filterSetUpdateUrl,
    filterSetDestroyUrl,
    isAdmin = false,
    currentUserId = null,
    onGenerated,
    prefillRequest,
    onPrefillHandled,
    schedulesStoreUrl,
    schedulePreviewUrl,
    scheduleDefaultTimezone,
}) {
    const [configuringReport, setConfiguringReport] = useState(null);
    const [prefill, setPrefill] = useState(null);
    const [openedViaRerun, setOpenedViaRerun] = useState(false);
    // Bumped on every open so the form resets even when re-opening the same
    // report via Configure (where the prefill is null both times).
    const [openNonce, setOpenNonce] = useState(0);
    // Rail filter: null = "All reports", else { tier, cat }.
    const [activeFilter, setActiveFilter] = useState(null);
    // An event admin can preview the landing as a standard user (hides the whole
    // Event-admin tier client-side). 'admin' = their full view; 'user' = preview.
    const [viewAs, setViewAs] = useState('admin');

    const modal = useModal();

    // Normalise the nested catalogue into ordered tiers of non-empty functional
    // groups: [{ tier, groups: [{ cat, entries }] }]. Handles both a functional
    // map and (defensively) a flat top-level list.
    const tiers = useMemo(() => normaliseTiers(catalogue), [catalogue]);

    const openForm = (report, options = null, viaRerun = false) => {
        setConfiguringReport(report);
        setPrefill(options);
        setOpenedViaRerun(viaRerun);
        setOpenNonce((nonce) => nonce + 1);
        modal.show();
    };

    // A Re-run request bubbled up from the Generated Reports tab opens the form
    // pre-filled with the stored options of the row being re-run.
    useEffect(() => {
        if (prefillRequest) {
            openForm(prefillRequest.report, prefillRequest.options || null, true);
            onPrefillHandled?.();
        }
    }, [prefillRequest]);

    const isActive = (tier, cat) => activeFilter && activeFilter.tier === tier && activeFilter.cat === cat;

    // The "Viewing as" toggle only appears when there is an Event-admin tier to
    // hide. Previewing as a standard user drops that tier from the rail and list
    // (a view filter, not a permission change — the data is still the admin's).
    const canPreviewAsUser = tiers.some((t) => t.tier === 'event_admin');
    const roleTiers = viewAs === 'user' ? tiers.filter((t) => t.tier !== 'event_admin') : tiers;

    const changeView = (nextView) => {
        setViewAs(nextView);
        if (nextView === 'user' && activeFilter && activeFilter.tier === 'event_admin') {
            setActiveFilter(null);
        }
    };

    // Sections to render: all of the in-view tiers, or (when a rail category is
    // selected) just the matching tier, narrowed to the matching functional group.
    const filteredTiers = roleTiers.filter((t) => !activeFilter || t.tier === activeFilter.tier);
    const visibleTiers = filteredTiers.map((t) => ({
        ...t,
        groups: t.groups.filter((g) => !activeFilter || g.cat === activeFilter.cat),
    }));

    const hasReports = visibleTiers.some((t) => t.groups.length > 0);

    return (
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-5 tw-items-start">
            {/* Category rail */}
            <div className="tw-w-full md:tw-w-64 tw-flex-shrink-0 md:tw-sticky md:tw-top-24 tw-self-start tw-bg-white tw-border tw-border-grey tw-rounded-2xl tw-p-3 tw-space-y-1">
                {canPreviewAsUser ? (
                    <div className="tw-mb-2 tw-bg-charcoal-10 tw-rounded-xl tw-p-3">
                        <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-charcoal-50 tw-mb-2">{$t('Viewing as')}</div>
                        <SegmentedControl
                            variant="dark"
                            options={[
                                { value: 'admin', label: $t('Event admin') },
                                { value: 'user', label: $t('Standard user') },
                            ]}
                            value={viewAs}
                            onChange={changeView}
                        />
                    </div>
                ) : null}
                <RailItem
                    label={$t('All reports')}
                    count={roleTiers.reduce((sum, t) => sum + t.groups.reduce((s, g) => s + g.entries.length, 0), 0)}
                    active={activeFilter === null}
                    onClick={() => setActiveFilter(null)}
                />
                {roleTiers.map((t) => (
                    <React.Fragment key={t.tier}>
                        <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-charcoal-50 tw-px-3 tw-pt-3 tw-pb-0.5">
                            {$t(RAIL_GROUP_LABELS[t.tier] || TOP_LEVEL_LABELS[t.tier] || t.tier)}
                        </div>
                        {t.groups.map((g) => (
                            <RailItem
                                key={`${t.tier}:${g.cat}`}
                                label={$t(FUNCTIONAL_LABELS[g.cat] || g.cat)}
                                count={g.entries.length}
                                active={isActive(t.tier, g.cat)}
                                onClick={() => setActiveFilter({ tier: t.tier, cat: g.cat })}
                            />
                        ))}
                    </React.Fragment>
                ))}
            </div>

            {/* Report list */}
            <div className="tw-flex-1 tw-min-w-0 tw-w-full">
                <FormatLegend />

                {hasReports ? (
                    visibleTiers.map((t) =>
                        t.groups.map((g) => (
                            <section key={`${t.tier}:${g.cat}`}>
                                <SectionHeader tier={t.tier} cat={g.cat} />
                                {g.entries.map((entry) => (
                                    <ReportCard
                                        key={entry.type || entry.url}
                                        entry={entry}
                                        isAdminTier={t.tier === 'event_admin'}
                                        onConfigure={() => openForm(entry)}
                                    />
                                ))}
                            </section>
                        )),
                    )
                ) : (
                    <div className="tw-p-10 tw-text-center tw-text-charcoal-50">{$t('No reports in this category.')}</div>
                )}

                <ReportParameterForm
                    modalId={MODAL_ID}
                    modalRef={modal.ref}
                    modalElement={modal.element}
                    report={configuringReport}
                    optionSources={optionSources}
                    storeUrl={storeUrl}
                    prefill={prefill}
                    openedViaRerun={openedViaRerun}
                    openNonce={openNonce}
                    filterSetsIndexUrl={filterSetsIndexUrl}
                    filterSetsStoreUrl={filterSetsStoreUrl}
                    filterSetUpdateUrl={filterSetUpdateUrl}
                    filterSetDestroyUrl={filterSetDestroyUrl}
                    isAdmin={isAdmin}
                    currentUserId={currentUserId}
                    onSuccess={onGenerated}
                    schedulesStoreUrl={schedulesStoreUrl}
                    schedulePreviewUrl={schedulePreviewUrl}
                    scheduleDefaultTimezone={scheduleDefaultTimezone}
                />
            </div>
        </div>
    );
}

/**
 * Flatten the nested catalogue into ordered tiers, each with its non-empty
 * functional groups. Tiers/groups keep the backend's declared order; empty tiers
 * and groups are dropped so the rail and list never show a bare heading.
 */
function normaliseTiers(catalogue) {
    return Object.keys(catalogue || {})
        .map((tier) => {
            const section = catalogue[tier];
            const groups = Array.isArray(section)
                ? section.length
                    ? [{ cat: null, entries: section }]
                    : []
                : Object.keys(section || {})
                      .filter((cat) => (section[cat] || []).length > 0)
                      .map((cat) => ({ cat, entries: section[cat] }));

            return { tier, groups };
        })
        .filter((t) => t.groups.length > 0);
}

function RailItem({ label, count, active, onClick }) {
    return (
        <button
            type="button"
            className={classNames(
                'tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-text-left tw-transition-colors',
                active ? 'tw-bg-blue tw-text-white tw-font-medium' : 'tw-text-charcoal hover:tw-bg-charcoal-10',
            )}
            onClick={onClick}
        >
            <span className="tw-truncate">{label}</span>
            <span className="tw-text-2xs tw-font-semibold tw-opacity-70 tw-ml-2">{count}</span>
        </button>
    );
}

/**
 * The format key shown once at the top of the list, identifying each card's output
 * format. Mirrors the per-card tag so the two read as one system.
 */
function FormatLegend() {
    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2 tw-p-3 tw-mb-4 tw-border tw-border-grey tw-rounded-xl tw-bg-white">
            <span className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-charcoal-50">{$t('Format key')}</span>
            <span className="tw-flex tw-items-center tw-gap-2">
                <Badge variant={FORMAT_BADGE.download.variant} icon={FORMAT_BADGE.download.icon} size="md">
                    {$t(FORMAT_BADGE.download.label)}
                </Badge>
                <span className="tw-text-sm tw-text-charcoal-75">{$t('Generated as a downloadable Excel file')}</span>
            </span>
            <span className="tw-flex tw-items-center tw-gap-2">
                <Badge variant={FORMAT_BADGE.screen.variant} icon={FORMAT_BADGE.screen.icon} size="md">
                    {$t(FORMAT_BADGE.screen.label)}
                </Badge>
                <span className="tw-text-sm tw-text-charcoal-75">{$t('Viewed live on screen')}</span>
            </span>
        </div>
    );
}

function SectionHeader({ tier, cat }) {
    const tierLabel = $t(TOP_LEVEL_LABELS[tier] || tier);
    const catLabel = cat ? $t(FUNCTIONAL_LABELS[cat] || cat) : null;

    return (
        <div className="tw-flex tw-items-center tw-gap-2 tw-mt-5 tw-mb-2.5 first:tw-mt-0">
            <span className={classNames('tw-w-1 tw-h-3.5 tw-rounded', tier === 'event_admin' ? 'tw-bg-charcoal' : 'tw-bg-blue')} />
            <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                {catLabel ? `${tierLabel} · ${catLabel}` : tierLabel}
            </span>
        </div>
    );
}

/**
 * One uniform card for every report, migrated or legacy. Configure opens the
 * generic parameter form for centre-generated reports (migrated cards whose
 * launchType is 'configure'); for context-launched reports (e.g. Actuals vs
 * Budget) and legacy link-outs it navigates to the report's own screen.
 */
function ReportCard({ entry, isAdminTier, onConfigure }) {
    const title = entry.name || entry.label;
    // A report viewable on screen and generated as a file lists both formats.
    const formats = (entry.formats || [entry.format]).map((key) => FORMAT_BADGE[key]).filter(Boolean);
    // Migrated cards carry a `type`; only centre-generated ones open the form.
    const opensForm = Boolean(entry.type) && entry.launchType === 'configure';
    const targetUrl = entry.launchRoute || entry.url || null;

    const handleClick = () => {
        if (opensForm) {
            onConfigure();
        } else if (targetUrl) {
            window.location.href = targetUrl;
        }
    };

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-4 tw-mb-2.5 tw-border tw-border-grey tw-rounded-xl tw-bg-white">
            <div className="tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-font-semibold tw-text-charcoal tw-truncate">{title}</span>
                    {isAdminTier ? (
                        <Badge variant="neutral" icon={LockIcon} size="md">
                            {$t('Event admin')}
                        </Badge>
                    ) : null}
                </div>
                {entry.description ? <div className="tw-text-sm tw-text-charcoal-75 tw-mt-1">{entry.description}</div> : null}
                {formats.length > 0 ? (
                    <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1.5">
                        {formats.map((format) => (
                            <Badge key={format.label} variant={format.variant} icon={format.icon} size="md">
                                {$t(format.label)}
                            </Badge>
                        ))}
                    </div>
                ) : null}
            </div>
            <div className="tw-flex-shrink-0">
                <Button variant="primary" small onClick={handleClick}>
                    {$t('Configure')}
                </Button>
            </div>
        </div>
    );
}
